import { memo, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Response } from '@/components/ui/shadcn-io/ai/response';
import { type QueueTodo } from '@/components/ui/shadcn-io/ai/queue';
import { AtomLoader } from '@/components/ui/atom-loader';
import { ContentSegment, ParsedTool } from './message-parser';
import type { StreamPhase } from './chat-types';
import { isStopNotice, isStoppedStatus, NO_REPLY_TEXT, STOP_NOTICE_TEXT } from './reply-notices';
import {
    DelegationTaskPanel,
    SubagentWorkPanel,
    isTaskDelegationTool,
} from '@/components/aios-command/chat/panels';
import { readDelegationPanelNameFromToolInput } from '@/utils/command-subagent-nesting';
import type { SubagentRun } from '@/utils/command-subagent-nesting';
import { SelectionOptionTiles } from './selection-option-tiles';
import { SkillChatChip } from '@/components/command-skills/skill-chat-chip';
import { CommandV2StreamErrorBanner } from '@/components/command-team-v2/cards/command-v2-stream-error-banner';
import { classifySkillTool, collapseSkillDisplay } from '@/components/command-skills/skill-chat';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { SelectionOption, SelectionRequest } from '@/types/command-selection';

interface AssistantMessageContentProps {
    content: string;
    contentSegments?: ContentSegment[];
    tools?: ParsedTool[];
    isStreaming?: boolean;
    messageId: string;
    status?: string;
    streamError?: string;
    selectionDisabled?: boolean;
    onPickSelection?: (option: SelectionOption, request: SelectionRequest) => void;
    /** Command v2 uses Thinking… dots before content; suppress trailing atom loader while streaming. */
    hideStreamingLoader?: boolean;
    /** Thin typing caret at the end of the last text block while Command is streaming. */
    showTypingCaret?: boolean;
    /** Latest `status` phase while streaming; labels the live activity line. */
    phase?: StreamPhase;
}

export function getAssistantErrorText(
    contentSegments?: ContentSegment[],
    streamError?: string
): string | undefined {
    if (streamError?.trim()) return streamError;
    return contentSegments?.find((segment) => segment.type === 'error')?.content;
}

function parseMaybeJson(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
}

function readTodoStatus(value: unknown): NonNullable<QueueTodo['status']> {
    if (value === 'completed' || value === 'in_progress' || value === 'pending') {
        return value;
    }
    return 'pending';
}

function isWriteTodosTool(type: string | undefined): boolean {
    return Boolean(type && /write_todos/i.test(type));
}

function isTodoRecord(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const record = value as Record<string, unknown>;
    return 'content' in record || 'title' in record || 'text' in record || 'status' in record;
}

function looksLikeTodoTitle(title: string): boolean {
    const trimmed = title.trim();
    if (trimmed.length < 2) return false;
    if (!/[A-Za-z]/.test(trimmed)) return false;
    if (/^[{[]/.test(trimmed)) return false;
    return true;
}

function readWriteTodos(input: unknown): QueueTodo[] {
    const parsed = parseMaybeJson(input);
    let raw: unknown = parsed;

    if (Array.isArray(parsed)) {
        raw = parsed;
    } else if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const nested = parseMaybeJson(record.todos ?? record.items ?? record.todo_list ?? record.tasks);
        if (Array.isArray(nested)) {
            raw = nested;
        } else if (isTodoRecord(nested)) {
            raw = [nested];
        } else if (nested && typeof nested === 'object') {
            const values = Object.values(nested as Record<string, unknown>).filter(isTodoRecord);
            raw = values.length > 0 ? values : [];
        } else if (isTodoRecord(record)) {
            raw = [record];
        } else {
            raw = [];
        }
    }

    if (!Array.isArray(raw)) return [];

    const todos = raw.flatMap((item, i) => {
        if (typeof item === 'string') {
            return looksLikeTodoTitle(item) ? [{ id: String(i), title: item.trim(), status: 'pending' as const }] : [];
        }
        if (!isTodoRecord(item)) return [];
        const title = String(item.content ?? item.title ?? item.text ?? '').trim();
        if (!looksLikeTodoTitle(title)) return [];
        return [
            {
                id: String(item.id ?? i),
                title,
                status: readTodoStatus(item.status),
            },
        ];
    });

    const activeIndex = todos.findIndex((todo) => todo.status === 'in_progress');
    if (activeIndex === -1) return todos;
    return todos.map((todo, index) =>
        todo.status === 'in_progress' && index !== activeIndex
            ? { ...todo, status: 'pending' }
            : todo
    );
}

/** Same note as the full-page chat, so both views show a reply's error the same way. */
function MessageErrorBanner({ text }: { text: string }) {
    return <CommandV2StreamErrorBanner text={text} />;
}

/** The end of a reply the user stopped ("User has interrupted."): a quiet note, not an error. */
export function StopNoticeLine({ text }: { text: string }) {
    return <p className="text-sm text-muted-foreground">{text}</p>;
}

/** A reply's end message: muted when the user stopped it, the error banner otherwise. */
function EndMessage({ text, reason }: { text: string; reason?: string }) {
    return isStopNotice(text, reason) ? <StopNoticeLine text={text} /> : <MessageErrorBanner text={text} />;
}

/**
 * Clean streaming content - remove code block markers and other artifacts
 */


/**
 * Clean tool output - remove code block markers
 */
function cleanToolOutput(output: string): string {
    return output;
}

/**
 * Convert Python dict string to valid JSON
 * Handles: single quotes, None, True, False, escaped quotes, and nested apostrophes
 */
function pythonDictToJson(pythonStr: string): string {
    let result = '';
    let inString = false;
    let stringChar = '';
    let i = 0;

    while (i < pythonStr.length) {
        const char = pythonStr[i];
        const nextChar = pythonStr[i + 1];

        if (!inString) {
            if (char === "'" || char === '"') {
                inString = true;
                stringChar = char;
                result += '"'; // Always use double quotes
            } else if (pythonStr.slice(i, i + 4) === 'None') {
                result += 'null';
                i += 3;
            } else if (pythonStr.slice(i, i + 4) === 'True') {
                result += 'true';
                i += 3;
            } else if (pythonStr.slice(i, i + 5) === 'False') {
                result += 'false';
                i += 4;
            } else {
                result += char;
            }
        } else {
            // Inside a string
            if (char === '\\' && nextChar === '"') {
                // Already escaped double quote - keep it escaped
                result += '\\"';
                i++;
            } else if (char === '\\' && nextChar === "'") {
                // Escaped single quote - just output the single quote
                result += "'";
                i++;
            } else if (char === '\\' && nextChar === '\\') {
                // Escaped backslash
                result += '\\\\';
                i++;
            } else if (char === '\\' && nextChar) {
                // Other escape sequence - keep it
                result += char + nextChar;
                i++;
            } else if (char === stringChar) {
                // End of string
                inString = false;
                result += '"';
            } else if (char === '"' && stringChar === "'") {
                // Double quote inside single-quoted string - escape it
                result += '\\"';
            } else if (char === "'" && stringChar === '"') {
                // Single quote (apostrophe) inside double-quoted string - keep as is
                result += "'";
            } else {
                result += char;
            }
        }
        i++;
    }

    return result;
}

/**
 * Pre-process string to normalize escape sequences
 */
function normalizeEscapes(str: string): string {
    // Handle mixed escaping: \" inside single-quoted strings
    // This pattern: 'text \"quoted\" text' should become "text \"quoted\" text"
    return str
        // First, temporarily replace \\" with a placeholder
        .replace(/\\\\"/g, '<<<ESCAPED_QUOTE>>>')
        // Replace \" that's inside strings
        .replace(/\\"([^"]*?)\\"/g, '"$1"')
        // Restore escaped quotes
        .replace(/<<<ESCAPED_QUOTE>>>/g, '\\"');
}

/**
 * Try to parse and format JSON from observation content
 * Python dict format: {'key': 'value'} -> {"key": "value"}
 */
function tryParseObservationJson(content: string): any | null {
    if (!content) return null;

    const cleaned = content.trim();

    // First try direct JSON parse
    try {
        return JSON.parse(cleaned);
    } catch {
        // Not valid JSON
    }

    // Pre-process to normalize escape sequences
    const normalized = normalizeEscapes(cleaned);

    // Try converting Python dict format to JSON
    try {
        const jsonStr = pythonDictToJson(normalized);
        return JSON.parse(jsonStr);
    } catch {
        // Still not valid
    }

    // Try with original string
    try {
        const jsonStr = pythonDictToJson(cleaned);
        return JSON.parse(jsonStr);
    } catch {
        // Still not valid
    }

    // Try a regex-based approach for simple cases
    try {
        let simple = cleaned
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        // Replace single quotes with double quotes more carefully
        // Match string boundaries
        simple = simple.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (match, content) => {
            // Escape any unescaped double quotes in the content
            const escaped = content.replace(/(?<!\\)"/g, '\\"');
            return `"${escaped}"`;
        });

        return JSON.parse(simple);
    } catch {
        return null;
    }
}

/**
 * Format JSON value for display with syntax highlighting
 */
function formatJsonValue(value: any, indent: number = 0): React.ReactNode {
    const indentStr = '  '.repeat(indent);

    if (value === null) {
        return <span className="text-orange-500 dark:text-orange-400">null</span>;
    }

    if (typeof value === 'boolean') {
        return <span className="text-purple-600 dark:text-purple-400">{value ? 'true' : 'false'}</span>;
    }

    if (typeof value === 'number') {
        return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
    }

    if (typeof value === 'string') {
        // Truncate long strings
        const displayStr = value.length > 100 ? value.slice(0, 100) + '...' : value;
        return <span className="text-green-600 dark:text-green-400">&quot;{displayStr}&quot;</span>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return <span>[]</span>;
        return (
            <span>
                {'[\n'}
                {value.map((item, idx) => (
                    <span key={idx}>
                        {indentStr}  {formatJsonValue(item, indent + 1)}
                        {idx < value.length - 1 ? ',\n' : '\n'}
                    </span>
                ))}
                {indentStr}{']'}
            </span>
        );
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 0) return <span>{'{}'}</span>;
        return (
            <span>
                {'{\n'}
                {entries.map(([key, val], idx) => (
                    <span key={key}>
                        {indentStr}  <span className="text-sky-600 dark:text-sky-400">&quot;{key}&quot;</span>: {formatJsonValue(val, indent + 1)}
                        {idx < entries.length - 1 ? ',\n' : '\n'}
                    </span>
                ))}
                {indentStr}{'}'}
            </span>
        );
    }

    return String(value);
}

/**
 * Render formatted tool output - detects JSON and formats it nicely
 */
function FormattedToolOutput({ output }: { output: string }) {
    const cleaned = cleanToolOutput(output);
    const parsed = tryParseObservationJson(cleaned);

    if (parsed) {
        const hasData = parsed.data !== undefined;
        const hasError = parsed.error && parsed.error !== null;

        return (
            <div>
                {/* Main data */}
                <div className="font-mono text-xs p-4 overflow-x-auto whitespace-pre text-foreground">
                    {hasData ? formatJsonValue(parsed.data) : formatJsonValue(parsed)}
                </div>

                {/* Error message if any */}
                {hasError && (
                    <div className="text-xs text-destructive border-t border-border p-3">
                        {String(parsed.error)}
                    </div>
                )}
            </div>
        );
    }

    // Not JSON, show as plain text with nice formatting
    return (
        <div className="font-mono text-xs p-4 overflow-x-auto whitespace-pre-wrap break-words text-foreground">
            {cleaned}
        </div>
    );
}


function fallbackDelegationRun(tool: ParsedTool, isExecuting: boolean): SubagentRun {
    const name =
        readDelegationPanelNameFromToolInput(tool.input as Record<string, unknown>) ??
        'subagent';
    return {
        subagentCallId: tool.toolCallId ?? 'delegation-pending',
        name,
        status: isExecuting ? 'running' : 'completed',
        error: tool.errorText ?? null,
        nestedSegments: [],
    };
}

type StepStatus = 'pending' | 'running' | 'done' | 'failed';

/** A step in Command's work: the note it wrote before acting, plus the tool calls it made */
interface WorkStep {
    key: string;
    note?: string;
    tools: ParsedTool[];
    /** Note Command is writing before its next tool call; no tools yet */
    pending?: boolean;
    /** The plan item Command had in progress when this step started */
    todoId?: string;
    todoIndex?: number;
}

/** One line in the steps card: a plan item, or (with no plan) a step as it happened */
interface CardItem {
    key: string;
    title: string;
    status: StepStatus;
    steps: WorkStep[];
}

interface PlanTodo {
    id: string;
    title: string;
    status: 'pending' | 'in_progress' | 'completed';
}

interface AsideSegment {
    idx: number;
    segment: ContentSegment;
}

function isChoiceTool(type: string | undefined): boolean {
    return Boolean(type && /ask_user_choice/i.test(type));
}

function getToolStatus(tool: ParsedTool, isStreaming?: boolean): StepStatus {
    if (tool.state === 'output-error') return 'failed';
    if (isStreaming && (tool.state === 'input-streaming' || tool.state === 'input-available')) return 'running';
    return 'done';
}

function getStepStatus(step: WorkStep, isStreaming?: boolean): StepStatus {
    if (step.tools.length === 0) return isStreaming ? 'running' : 'done';
    const statuses = step.tools.map((tool) => getToolStatus(tool, isStreaming));
    if (statuses.includes('running')) return 'running';
    return statuses[statuses.length - 1];
}

/** "tool-some_tool_name" → "Some tool name" */
function formatToolName(type: string): string {
    const words = type
        .replace(/^tool[_-]?/i, '')
        .replace(/^aios[_-]/i, '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    if (!words) return 'Tool';
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function skillActionLabel(tool: ParsedTool): string | null {
    const info = classifySkillTool(tool);
    if (!info || info.kind === 'hide') return null;
    const action = info.kind === 'prepare' ? 'Preparing skill' : 'Using skill';
    return `${action} · ${info.name}`;
}

function stepTitle(step: WorkStep): string {
    if (step.note) return step.note;
    const onlySkills = step.tools.length > 0 && step.tools.every((tool) => classifySkillTool(tool));
    const skillLabel = onlySkills ? step.tools.map(skillActionLabel).find(Boolean) : undefined;
    if (skillLabel) return skillLabel;
    const first = step.tools[0];
    if (!first) return 'Working';
    return formatToolName(first.type) + (step.tools.length > 1 ? ` and ${step.tools.length - 1} more` : '');
}

/** A one-line note ("Pulling the meeting detail…"), not the start of a formatted answer */
function looksLikeNote(text: string): boolean {
    const trimmed = text.trim();
    return trimmed.length > 0 && trimmed.length <= 240 && !trimmed.includes('\n') && !/^([#>*|-]|\d+\.)/.test(trimmed);
}

/** Items from a write_todos call (Command's own plan) */
function readPlan(tool: ParsedTool): PlanTodo[] {
    return readWriteTodos(tool.input).map((todo) => ({
        id: todo.id,
        title: todo.title,
        status: todo.status === 'completed' || todo.status === 'in_progress' ? todo.status : 'pending',
    }));
}

function readChoiceRequest(tool: ParsedTool): SelectionRequest | null {
    const input = (tool.input ?? {}) as Record<string, unknown>;
    const rawOptions = Array.isArray(input.options) ? input.options : [];
    const options: SelectionOption[] = [];
    for (const opt of rawOptions) {
        if (!opt || typeof opt !== 'object') continue;
        const record = opt as Record<string, unknown>;
        const value = String(record.value ?? record.id ?? '').trim();
        const label = String(record.label ?? record.name ?? value).trim();
        if (!label) continue;
        const subtitle =
            typeof record.subtitle === 'string'
                ? record.subtitle
                : typeof record.description === 'string'
                  ? record.description
                  : undefined;
        options.push({
            value: value || label,
            label,
            ...(subtitle ? { subtitle } : {}),
            recommended: Boolean(record.recommended),
        });
    }
    if (options.length === 0) return null;
    return {
        toolCallId: tool.toolCallId,
        name: tool.type,
        prompt: typeof input.prompt === 'string' ? input.prompt : undefined,
        options,
        multiSelect: Boolean(input.multi_select),
    };
}

/**
 * Split segments into the work Command did (steps), the answer it gave, and
 * anything that stays outside the card (errors, choices, selection tiles).
 * Text written before a tool call becomes that step's title; text after the
 * last tool call is the answer. While Command is still working, a short
 * trailing note is shown as the next step straight away.
 *
 * When Command writes a plan (write_todos), each step is filed under the plan
 * item it had in progress, and the latest plan is returned as `plan`.
 */
function groupSegments(segments: ContentSegment[], isStreaming?: boolean) {
    const isPlan = (segment: ContentSegment) =>
        segment.type === 'tool' && isWriteTodosTool(segment.tool?.type);
    const isChoice = (segment: ContentSegment) =>
        segment.type === 'tool' && isChoiceTool(segment.tool?.type);
    const isStepTool = (segment: ContentSegment) =>
        segment.type === 'tool' && Boolean(segment.tool) && !isPlan(segment) && !isChoice(segment);

    let lastToolIdx = -1;
    segments.forEach((segment, index) => {
        if (isStepTool(segment)) lastToolIdx = index;
    });

    const steps: WorkStep[] = [];
    const aside: AsideSegment[] = [];
    let pendingNotes: string[] = [];
    let plan: PlanTodo[] | undefined;
    let activeTodoId: string | undefined;
    let activeTodoIndex: number | undefined;

    segments.forEach((segment, index) => {
        if (segment.type === 'error' || segment.type === 'selection' || isChoice(segment)) {
            aside.push({ idx: index, segment });
            return;
        }
        if (isPlan(segment) && segment.tool) {
            plan = readPlan(segment.tool);
            const inProgress = plan.findIndex((todo) => todo.status === 'in_progress');
            if (inProgress >= 0) {
                activeTodoId = plan[inProgress].id;
                activeTodoIndex = inProgress;
            }
            return;
        }
        if (index > lastToolIdx) return;
        if (segment.type === 'text') {
            if (segment.content?.trim()) pendingNotes.push(segment.content.trim());
            return;
        }
        if (!isStepTool(segment) || !segment.tool) return;
        const last = steps[steps.length - 1];
        if (last && pendingNotes.length === 0 && last.todoId === activeTodoId) {
            last.tools.push(segment.tool);
        } else {
            steps.push({
                key: segment.tool.toolCallId || `step-${index}`,
                note: pendingNotes.length > 0 ? pendingNotes.join('\n\n') : undefined,
                tools: [segment.tool],
                todoId: activeTodoId,
                todoIndex: activeTodoIndex,
            });
        }
        pendingNotes = [];
    });

    let answer = segments
        .map((segment, idx) => ({ segment, idx }))
        .filter(({ segment, idx }) => idx > lastToolIdx && segment.type === 'text' && segment.content);

    const trailingText = answer.map(({ segment }) => segment.content ?? '').join('\n\n');
    const planDone = Boolean(plan && plan.length > 0 && plan.every((todo) => todo.status === 'completed'));
    const hasWork = Boolean(plan && plan.length > 0) || steps.length > 0;
    if (isStreaming && !planDone && hasWork && looksLikeNote(trailingText)) {
        steps.push({
            key: 'pending',
            note: trailingText.trim(),
            tools: [],
            pending: true,
            todoId: activeTodoId,
            todoIndex: activeTodoIndex,
        });
        answer = [];
    }

    return { steps, answer, plan, aside };
}

/**
 * The lines of the steps card. With a plan, every plan item shows from the
 * start (upcoming ones as empty circles); without one, steps appear as they happen.
 */
function buildCardItems(steps: WorkStep[], plan: PlanTodo[] | undefined, isStreaming?: boolean): CardItem[] {
    if (plan && plan.length > 0) {
        const ids = new Set(plan.map((todo) => todo.id));
        return plan.map((todo, index) => {
            const todoSteps = steps.filter((step) =>
                step.todoId !== undefined && ids.has(step.todoId) ? step.todoId === todo.id : step.todoIndex === index
            );
            const lastStatus = todoSteps.length > 0 ? getStepStatus(todoSteps[todoSteps.length - 1], isStreaming) : undefined;
            const status: StepStatus =
                todo.status === 'completed'
                    ? lastStatus === 'failed'
                        ? 'failed'
                        : 'done'
                    : todo.status === 'in_progress' && isStreaming
                      ? 'running'
                      : 'pending';
            return { key: `todo-${todo.id}`, title: todo.title, status, steps: todoSteps };
        }).filter((item) => item.steps.length > 0 || item.status !== 'done');
    }
    return steps.map((step) => ({
        key: step.key,
        title: stepTitle(step),
        status: getStepStatus(step, isStreaming),
        steps: [step],
    }));
}

function StepIcon({ status }: { status: StepStatus }) {
    if (status === 'running') {
        return (
            <span className="flex size-5 items-center justify-center">
                <span className="size-3.5 animate-spin rounded-full border-[1.5px] border-muted-foreground/25 border-t-foreground" />
            </span>
        );
    }
    if (status === 'pending') {
        return (
            <span className="flex size-5 items-center justify-center">
                <span className="size-3.5 rounded-full border-[1.5px] border-muted-foreground/40" />
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <span className="flex size-5 items-center justify-center">
                <span className="flex size-3.5 items-center justify-center rounded-full bg-destructive">
                    <XIcon className="size-2.5 text-white" strokeWidth={3} />
                </span>
            </span>
        );
    }
    return (
        <span className="flex size-5 items-center justify-center">
            <span className="flex size-3.5 items-center justify-center rounded-full bg-foreground">
                <CheckIcon className="size-2.5 text-background" strokeWidth={3.5} />
            </span>
        </span>
    );
}

function DelegationTools({ tool, isStreaming }: { tool: ParsedTool; isStreaming?: boolean }) {
    const isExecuting =
        isStreaming === true &&
        (tool.state === 'input-streaming' || tool.state === 'input-available');
    const runs =
        tool.subagentRuns?.length && tool.subagentRuns.length > 0
            ? tool.subagentRuns
            : [fallbackDelegationRun(tool, isExecuting)];

    return (
        <div className="space-y-2 px-2 py-2">
            {runs.map((run) => (
                <DelegationTaskPanel
                    key={run.subagentCallId}
                    tool={tool}
                    run={run}
                    isStreaming={isStreaming}
                />
            ))}
        </div>
    );
}

/** One tool call inside a step; click to see its request and response */
function ToolCallRow({ tool, isStreaming }: { tool: ParsedTool; isStreaming?: boolean }) {
    const [open, setOpen] = useState(false);
    const status = getToolStatus(tool, isStreaming);
    const hasInput = tool.input && Object.keys(tool.input).length > 0;
    const hasOutput = tool.output != null && tool.output !== '';
    const hasDetail = hasInput || hasOutput || Boolean(tool.errorText);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <button
                type="button"
                onClick={() => hasDetail && setOpen((prev) => !prev)}
                className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors',
                    hasDetail ? 'cursor-pointer hover:bg-muted/60' : 'cursor-default'
                )}
            >
                <span className="truncate font-medium text-foreground">{formatToolName(tool.type)}</span>
                {status === 'running' && <span className="text-iai-teal">Running</span>}
                {status === 'failed' && <span className="text-destructive">Failed</span>}
                {hasDetail && (
                    <ChevronRightIcon
                        className={cn('ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
                    />
                )}
            </button>
            <CollapsibleContent className="space-y-2 px-3 pb-3 data-[state=open]:animate-in data-[state=open]:fade-in-0">
                {hasInput && (
                    <div className="max-h-[360px] overflow-auto rounded-lg bg-muted/50 text-xs">
                        <div className="px-3 pt-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
                            REQUEST
                        </div>
                        <FormattedToolOutput output={JSON.stringify(tool.input, null, 2)} />
                    </div>
                )}
                {hasOutput && (
                    <div className="max-h-[360px] overflow-auto rounded-lg bg-muted/50 text-xs">
                        <div className="px-3 pt-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
                            RESPONSE
                        </div>
                        <FormattedToolOutput output={String(tool.output)} />
                    </div>
                )}
                {tool.errorText && (
                    <div className="rounded-lg bg-destructive/10 p-2 text-xs text-destructive">{tool.errorText}</div>
                )}
                {status === 'running' && !hasOutput && (
                    <div className="px-1 text-xs text-muted-foreground">Waiting for a response…</div>
                )}
            </CollapsibleContent>
            {tool.subagentRuns?.map((run) => (
                <div key={run.subagentCallId} className="px-2 pb-2">
                    <SubagentWorkPanel run={run} isStreaming={isStreaming} />
                </div>
            ))}
        </Collapsible>
    );
}

/** Tool rows inside a step. Skill reads and prepares show as a chip instead of a raw tool call. */
function StepToolList({ tools, isStreaming }: { tools: ParsedTool[]; isStreaming?: boolean }) {
    const pieces = collapseSkillDisplay(tools.map((tool) => ({ type: 'tool' as const, tool })));
    if (pieces.length === 0) return null;
    return (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
            {pieces.map((piece) => {
                if (piece.kind === 'skill') {
                    return (
                        <div key={piece.key} className="px-2 py-1">
                            <SkillChatChip name={piece.name} variant={piece.variant} />
                        </div>
                    );
                }
                const tool = piece.segment.tool;
                if (!tool) return null;
                return isTaskDelegationTool(tool) ? (
                    <DelegationTools key={tool.toolCallId || piece.index} tool={tool} isStreaming={isStreaming} />
                ) : (
                    <ToolCallRow key={tool.toolCallId || piece.index} tool={tool} isStreaming={isStreaming} />
                );
            })}
        </div>
    );
}

/**
 * What's behind a card line, shown when it's opened: the notes Command wrote
 * and the tool calls it made for it.
 */
function StepToolCards({ steps, title, isStreaming }: { steps: WorkStep[]; title?: string; isStreaming?: boolean }) {
    if (steps.length === 0) {
        return <div className="mb-2 mt-1 text-xs text-muted-foreground">Nothing run for this step yet</div>;
    }
    return (
        <div className="mb-2 mt-1 space-y-2 animate-in fade-in-0">
            {steps.map((step) => (
                <div key={step.key} className="space-y-1">
                    {step.note && step.note !== title && (
                        <p className="whitespace-pre-line text-xs text-muted-foreground">{step.note}</p>
                    )}
                    {step.tools.length === 0 ? (
                        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
                            <div className="px-3 py-2 text-xs text-muted-foreground">Choosing a tool…</div>
                        </div>
                    ) : (
                        <StepToolList tools={step.tools} isStreaming={isStreaming} />
                    )}
                </div>
            ))}
        </div>
    );
}

/**
 * One line in the steps card: spinner (current), tick (done), empty circle
 * (still to do) or cross (failed), then the title. Click it to see what
 * Command did for it.
 */
function StepRow({
    item,
    isStreaming,
    open,
    onToggle,
}: {
    item: CardItem;
    isStreaming?: boolean;
    open: boolean;
    onToggle: () => void;
}) {
    return (
        <div>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className={cn(
                    'group/step flex min-h-7 w-full items-center gap-2 text-left text-sm leading-5',
                    item.status === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                )}
            >
                <StepIcon status={item.status} />
                <span className={cn('min-w-0 flex-1 py-1', open ? 'whitespace-pre-line' : 'truncate')}>{item.title}</span>
                <ChevronRightIcon
                    className={cn(
                        'size-3.5 shrink-0 text-muted-foreground transition-transform group-hover/step:text-foreground',
                        open && 'rotate-90'
                    )}
                />
            </button>
            {open && (
                <div className="pl-7">
                    <StepToolCards steps={item.steps} title={item.title} isStreaming={isStreaming} />
                </div>
            )}
        </div>
    );
}

/** Every step, in a card, like a task list */
function StepsCard({
    items,
    isStreaming,
    openKeys,
    onToggle,
}: {
    items: CardItem[];
    isStreaming?: boolean;
    openKeys: Set<string>;
    onToggle: (key: string) => void;
}) {
    return (
        <div className="rounded-xl border border-border bg-background px-3 py-1.5">
            {items.map((item) => (
                <StepRow
                    key={item.key}
                    item={item}
                    isStreaming={isStreaming}
                    open={openKeys.has(item.key)}
                    onToggle={() => onToggle(item.key)}
                />
            ))}
        </div>
    );
}

/** Seconds since `key` last changed, ticking every second */
function useElapsedSeconds(key: string) {
    const [startedAt, setStartedAt] = useState(() => Date.now());
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        setStartedAt(Date.now());
        setNow(Date.now());
    }, [key]);
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, []);
    return Math.max(0, Math.floor((now - startedAt) / 1000));
}

const formatElapsed = (seconds: number) =>
    seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const phaseLabel = (phase?: StreamPhase) => (phase === 'finishing' ? 'Finishing up…' : 'Thinking…');

/**
 * Under the steps card while Command works: the thinking icon, then the
 * action it's running right now (or the backend's phase) and for how long.
 */
function LiveActivityLine({
    step,
    isStreaming,
    phase,
    open,
    onToggle,
}: {
    step?: WorkStep;
    isStreaming?: boolean;
    phase?: StreamPhase;
    open: boolean;
    onToggle: () => void;
}) {
    const runningTool = step?.tools.filter((tool) => getToolStatus(tool, isStreaming) === 'running').at(-1);
    const label = (runningTool && skillActionLabel(runningTool)) || (runningTool ? formatToolName(runningTool.type) : phaseLabel(phase));
    const activityKey = runningTool
        ? runningTool.toolCallId
        : `${phase ?? 'thinking'}-${step?.key ?? 'start'}-${step?.tools.length ?? 0}`;
    const seconds = useElapsedSeconds(activityKey);
    const canOpen = Boolean(step);
    const isOpen = canOpen && open;

    return (
        <div>
            <button
                type="button"
                onClick={canOpen ? onToggle : undefined}
                disabled={!canOpen}
                aria-expanded={canOpen ? isOpen : undefined}
                className="flex h-7 items-center gap-2 text-left text-sm text-foreground disabled:cursor-default"
            >
                <AtomLoader variant="minimal" className="-ml-0.5 shrink-0" />
                <span className="truncate">{label}</span>
                {seconds > 0 && <span className="shrink-0 text-muted-foreground">{formatElapsed(seconds)}</span>}
                {canOpen && (
                    <ChevronRightIcon
                        className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                    />
                )}
            </button>
            {isOpen && step && <StepToolCards steps={[step]} isStreaming={isStreaming} />}
        </div>
    );
}

/** How long the answer text has to pause before the live line comes back under it. */
const TEXT_PAUSE_MS = 1500;

/**
 * Under the answer while it streams: hidden while text is arriving, back once no text has come
 * for a moment (Command is thinking again, or finishing up the title and suggestions).
 */
function PausedActivityLine({ textLength, phase }: { textLength: number; phase?: StreamPhase }) {
    const [paused, setPaused] = useState(false);
    useEffect(() => {
        setPaused(false);
        const timer = window.setTimeout(() => setPaused(true), TEXT_PAUSE_MS);
        return () => window.clearTimeout(timer);
    }, [textLength]);
    if (!paused) return null;
    return (
        <div className="mt-2">
            <LiveActivityLine isStreaming phase={phase} open={false} onToggle={() => undefined} />
        </div>
    );
}

/*
 * When a new reply starts, every older reply folds its steps away. Each
 * WorkSteps that starts working bumps this counter; the others see it change
 * and close whatever the user had opened.
 */
let foldEpoch = 0;
const foldListeners = new Set<() => void>();
const subscribeFold = (listener: () => void) => {
    foldListeners.add(listener);
    return () => {
        foldListeners.delete(listener);
    };
};
const bumpFold = () => {
    foldEpoch += 1;
    foldListeners.forEach((listener) => listener());
    return foldEpoch;
};

/**
 * Command's work, laid out like a task list. While it works, a card lists
 * every step of its plan: a spinner on the current step, ticks on done ones,
 * empty circles on ones still to do. Under it, the thinking icon shows the
 * action running now. Once done, the card folds into "Worked through N steps".
 */
function WorkSteps({
    items,
    liveStep,
    isStreaming,
    phase,
}: {
    items: CardItem[];
    liveStep?: WorkStep;
    isStreaming?: boolean;
    phase?: StreamPhase;
}) {
    const [expanded, setExpanded] = useState(false);
    const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set());
    const [currentOpen, setCurrentOpen] = useState(false);

    const epoch = useSyncExternalStore(subscribeFold, () => foldEpoch, () => 0);
    const ownEpoch = useRef<number | null>(null);
    useEffect(() => {
        if (isStreaming) ownEpoch.current = bumpFold();
    }, [isStreaming]);
    useEffect(() => {
        if (ownEpoch.current === epoch) return;
        setExpanded(false);
        setOpenKeys(new Set());
        setCurrentOpen(false);
    }, [epoch]);

    const lastLiveItemKey = useRef<string | undefined>(undefined);
    if (isStreaming && liveStep) {
        lastLiveItemKey.current = items.find((item) => item.steps.includes(liveStep))?.key ?? lastLiveItemKey.current;
    }
    const wasStreaming = useRef(isStreaming);
    useEffect(() => {
        if (wasStreaming.current && !isStreaming && currentOpen) {
            const key = lastLiveItemKey.current;
            if (key && items.some((item) => item.key === key)) setOpenKeys((prev) => new Set(prev).add(key));
            setExpanded(true);
            setCurrentOpen(false);
        }
        wasStreaming.current = isStreaming;
    }, [isStreaming, currentOpen, items]);

    const toggleKey = (key: string) =>
        setOpenKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    if (isStreaming) {
        return (
            <div className="mb-3 w-full max-w-full space-y-2">
                {items.length > 0 && (
                    <StepsCard items={items} isStreaming={isStreaming} openKeys={openKeys} onToggle={toggleKey} />
                )}
                <LiveActivityLine
                    step={liveStep}
                    isStreaming={isStreaming}
                    phase={phase}
                    open={currentOpen}
                    onToggle={() => setCurrentOpen((prev) => !prev)}
                />
            </div>
        );
    }

    if (items.length === 0) return null;
    const failedCount = items.filter((item) => item.status === 'failed').length;

    return (
        <div className="mb-3 w-full max-w-full">
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                className="flex h-7 items-center gap-1.5 rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
                <span>
                    Worked through {items.length} {items.length === 1 ? 'step' : 'steps'}
                </span>
                {failedCount > 0 && <span className="text-destructive">· {failedCount} failed</span>}
                <ChevronDownIcon className={cn('size-4 transition-transform', !expanded && '-rotate-90')} />
            </button>
            {expanded && (
                <div className="mt-1">
                    <StepsCard items={items} openKeys={openKeys} onToggle={toggleKey} />
                </div>
            )}
        </div>
    );
}

function AsideBlock({
    segment,
    messageId,
    index,
    isStreaming,
    selectionDisabled,
    onPickSelection,
}: {
    segment: ContentSegment;
    messageId: string;
    index: number;
    isStreaming?: boolean;
    selectionDisabled?: boolean;
    onPickSelection?: (option: SelectionOption, request: SelectionRequest) => void;
}) {
    if (segment.type === 'error' && segment.content) {
        return (
            <div className="mt-3 first:mt-0">
                <EndMessage text={segment.content} reason={segment.reason} />
            </div>
        );
    }
    if (segment.type === 'selection' && segment.selection && onPickSelection) {
        const request = segment.selection;
        return (
            <SelectionOptionTiles
                key={`selection-${messageId}-${index}`}
                selection={request}
                disabled={selectionDisabled}
                onPick={(option) => onPickSelection(option, request)}
            />
        );
    }
    if (segment.type === 'tool' && segment.tool && isChoiceTool(segment.tool.type)) {
        const request = readChoiceRequest(segment.tool);
        if (request && onPickSelection) {
            return (
                <SelectionOptionTiles
                    key={`selection-tool-${segment.tool.toolCallId ?? index}`}
                    selection={request}
                    disabled={selectionDisabled}
                    onPick={(option) => onPickSelection(option, request)}
                />
            );
        }
        return <ToolCallRow tool={segment.tool} isStreaming={isStreaming} />;
    }
    return null;
}

/**
 * A reply's end message (an error, or the stop notice) shows where it happened: after everything the
 * reply got to. One that only lives in `streamError` goes last too, instead of above the reply.
 */
function withEndMessage(
    content: string,
    contentSegments: ContentSegment[] | undefined,
    streamError: string | undefined,
): ContentSegment[] | undefined {
    if (!streamError?.trim() || contentSegments?.some((segment) => segment.type === 'error')) return contentSegments;
    const body: ContentSegment[] = contentSegments?.length
        ? contentSegments
        : content?.trim()
          ? [{ type: 'text', content }]
          : [];
    return [...body, { type: 'error', content: streamError }];
}

/**
 * Render assistant message content with proper formatting
 * Uses contentSegments for ordered display - same format for streaming and database
 * During streaming: shows text as-is (no cleaning)
 * After streaming: cleans text (removes backticks, etc.)
 */
export const AssistantMessageContent = memo(function AssistantMessageContent({
    content,
    contentSegments: rawSegments,
    isStreaming,
    messageId,
    streamError,
    selectionDisabled,
    onPickSelection,
    hideStreamingLoader = false,
    showTypingCaret = false,
    status,
    phase,
}: AssistantMessageContentProps) {
    const contentSegments = withEndMessage(content, rawSegments, streamError);

    if (contentSegments && contentSegments.length > 0) {
        const { steps, answer, plan, aside } = groupSegments(contentSegments, isStreaming);
        const isWorking = Boolean(isStreaming) && answer.length === 0;
        const items = buildCardItems(steps, plan, isWorking);
        const firstAnswerIdx = answer[0]?.idx ?? Number.POSITIVE_INFINITY;
        const lastAnswerIdx = answer.length > 0 ? answer[answer.length - 1].idx : -1;
        const asideBefore = aside.filter((item) => item.idx < firstAnswerIdx);
        const asideAfter = aside.filter((item) => item.idx >= firstAnswerIdx);
        const showWork = items.length > 0 || isWorking;
        const answerLength = answer.reduce((total, { segment }) => total + (segment.content?.length ?? 0), 0);

        const renderAside = (item: AsideSegment) => (
            <AsideBlock
                key={`aside-${messageId}-${item.idx}`}
                segment={item.segment}
                messageId={messageId}
                index={item.idx}
                isStreaming={isStreaming}
                selectionDisabled={selectionDisabled}
                onPickSelection={onPickSelection}
            />
        );

        return (
            <>
                {showWork && (
                    <WorkSteps
                        items={items}
                        liveStep={steps[steps.length - 1]}
                        isStreaming={isWorking}
                        phase={phase}
                    />
                )}
                {asideBefore.map(renderAside)}
                {answer.map(({ segment, idx }) =>
                    segment.content ? (
                        <Response
                            key={`text-${idx}`}
                            className={cn(
                                'break-words overflow-wrap-anywhere',
                                showTypingCaret && idx === lastAnswerIdx && 'command-streaming-text'
                            )}
                            parseIncompleteMarkdown={Boolean(isStreaming)}
                        >
                            {segment.content}
                        </Response>
                    ) : null
                )}
                {isStreaming && !isWorking && <PausedActivityLine textLength={answerLength} phase={phase} />}
                {asideAfter.map(renderAside)}
            </>
        );
    }

    // Streaming with no segments yet — atom icon, "Thinking", and a timer.
    // The same line stays under the steps card once work starts.
    if (isStreaming && !hideStreamingLoader) {
        return <WorkSteps items={[]} isStreaming phase={phase} />;
    }

    // Fallback: Show raw content if no segments (for simple text messages)

        
        if (content) {
            return (
                <Response
                    className={cn(
                        'break-words overflow-wrap-anywhere',
                        showTypingCaret && 'command-streaming-text'
                    )}
                    parseIncompleteMarkdown={Boolean(isStreaming)}
                >
                    {content}
                </Response>
            );
        }

    // A finished reply with nothing in it still says what happened.
    if (!isStreaming && status !== 'GENERATING') {
        return isStoppedStatus(status) ? <StopNoticeLine text={STOP_NOTICE_TEXT} /> : <MessageErrorBanner text={NO_REPLY_TEXT} />;
    }

    return null;
}, (prevProps, nextProps) => {
    const segmentSignature = (segments?: ContentSegment[]) =>
        (segments ?? [])
            .map((seg) => {
                if (seg.type === 'error') return `e:${seg.content ?? ''}`;
                if (seg.type === 'text') return `t:${seg.content ?? ''}`;
                if (seg.type === 'selection') {
                    const opts = seg.selection?.options ?? [];
                    return `s:${opts.map((o) => o.value).join(',')}`;
                }
                const tool = seg.tool;
                const subagentSig =
                    tool?.subagentRuns
                        ?.map(
                            (run) =>
                                `${run.subagentCallId}:${run.status}:${run.nestedSegments.length}`,
                        )
                        .join(';') ?? '';
                return `u:${tool?.toolCallId ?? ''}:${tool?.state ?? ''}:${tool?.type ?? ''}:${tool?.output ?? ''}:${JSON.stringify(tool?.input ?? null)}:${subagentSig}`;
            })
            .join('|');

    const toolsSignature = (toolList?: ParsedTool[]) =>
        (toolList ?? [])
            .map((tool) => `${tool.toolCallId}:${tool.state}:${tool.type}:${tool.output ?? ''}`)
            .join('|');

    // Always re-render during streaming to show typing effect
    if (nextProps.isStreaming) return false;

    // After streaming, compare normally
    if (prevProps.isStreaming !== nextProps.isStreaming) return false;
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.streamError !== nextProps.streamError) return false;
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.selectionDisabled !== nextProps.selectionDisabled) return false;
    if (prevProps.onPickSelection !== nextProps.onPickSelection) return false;
    if (prevProps.contentSegments?.length !== nextProps.contentSegments?.length) return false;
    if (segmentSignature(prevProps.contentSegments) !== segmentSignature(nextProps.contentSegments)) return false;
    if (toolsSignature(prevProps.tools) !== toolsSignature(nextProps.tools)) return false;

    return prevProps.content === nextProps.content;
});

